import { unwrapResult } from '@reduxjs/toolkit'
import { remoteLogin } from '@user/store/authorization'
import {
  markRemoteLoginAttempted,
  selectRemoteLoginAttempted,
  selectRemoteLoginPending,
} from '@user/store/authorization/userSlice'

import { FC, useEffect } from 'react'
import { useAppDispatch } from '@user/store/hooks/useAppDispatch'
import { useNavigate } from 'react-router-dom'
import { updateLoadingState } from '@sales-center/common'
import { toast } from '@sales-center/components'
import { useSelector } from 'react-redux'
import { selectCurrentUser } from '@user/store/authorization'
import { guestRoleName } from '../Settings/Users/UpdateUser/constants'

type LoginMode = 'external' | 'sso'

interface ExternalLoginProps {
  mode: LoginMode
}

const useExternalLogin = (mode: LoginMode) => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const currentUser = useSelector(selectCurrentUser)
  const isGuestUser = currentUser?.roleName === guestRoleName

  const attempted = useSelector(selectRemoteLoginAttempted(mode))
  const pending = useSelector(selectRemoteLoginPending)

  useEffect(() => {
    if (attempted || pending) return

    // mark first so a remount won't re-dispatch
    dispatch(markRemoteLoginAttempted(mode))
    ;(async () => {
      dispatch(updateLoadingState('visible'))
      const result = await dispatch(remoteLogin())

      if (remoteLogin.fulfilled.match(result)) {
        const {
          payload: { redirectUrl },
        } = result

        const target =
          mode === 'sso'
            ? isGuestUser
              ? redirectUrl || '/scdashboard'
              : '/scdashboard'
            : redirectUrl

        navigate(target == redirectUrl ? '/' + target : target, {
          replace: true,
        }) // exactly one navigation

        dispatch(updateLoadingState('hidden'))
      } else {
        // allow retry on failure
        try {
          unwrapResult(result)
        } catch (e) {
          if (e instanceof Error) toast.error(e.message)
          else if (typeof e === 'string') toast.error(e)
          else toast.error('Unexpected error')
        } finally {
          dispatch(updateLoadingState('hidden'))
        }
      }
    })()

    return () => {}
  }, [attempted, pending, dispatch, navigate, isGuestUser, mode])
}

const ExternalLogin: FC<ExternalLoginProps> = ({ mode }) => {
  useExternalLogin(mode)
  return null
}

export default ExternalLogin
